import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';

// exporting as a named export to allow for easier testing
// this method is called immediately in this file to initialize Sentry
export function initializeSentry() {
  if (window.__sentry__) {
    const config = window.__sentry__ as Sentry.BrowserOptions;

    const denyUrls = [
      ...(config.denyUrls || []),
      /localhost:3000/,
      // This isn't covered by Sentry's built-in browser extension detection, for some reason
      /^chrome-extension:\/\//i,
    ];

    const ignoreErrors = [
      ...(config.ignoreErrors || []),
      /**
       * This covers 2 separate exceptions that we see but are out of our control:
       *
       * - "TypeError: Failed to construct 'Request': Request cannot be constructed from a URL that includes credentials: <url>"
       * - "TypeError: Failed to execute 'fetch' on 'Window': Request cannot be constructed from a URL that includes credentials: <url>"
       *
       * We don't ever build URLs that include a username and password in them, so these are likely caused by browser extensions,
       * bookmarklets, a user script, etc. The 3rd party code itself could be constructing `Request` objects or making `fetch` requests
       * with URLs containing credentials, overriding the native behavior of `fetch`, or even navigating to a Bitbucket page with
       * credentials inserted. `fetch` requests to relative URLs carry over the username and password used on the current page rather than
       * dropping them, which could inadvertently add those to the `fetch` requests our code tries to make.
       */
      'Request cannot be constructed from a URL that includes credentials',
      // (likely) Caused by ad-blocking in Firefox (only occurs for requests made to our metrics endpoint)
      'AbortError: The operation was aborted',
      // (likely) Caused by ad-blocking in IE11 (only occurs for requests made to our metrics endpoint)
      'Network request failed',
      // Caused by ad-blocking in Firefox
      'NetworkError when attempting to fetch resource',
      // Caused by ad-blocking in Safari and Edge
      'Resource blocked by content blocker',
      /**
       * This is thrown by `window.fetch`, but not for non-2XX responses. It's only thrown when fetch itself breaks, which could be
       * caused by:
       *
       *   - DNS issues
       *   - Bad HTTP headers
       *   - Malformed URL
       *   - The request was aborted by the browser (user navigates to another page before the request completes)
       *   - The request was blocked by an extension (i.e., an ad-blocker)
       *
       * Source: https://stackoverflow.com/questions/45383874/fetch-api-yields-typeerror-failed-to-fetch/47423883#47423883
       *
       * As of the date this comment was written, 97% of these errors are caused by requests to our metrics endpoint. This rules out
       * the first 3. The 4th is unlikely because it's not a slow request to make, which leaves ad-blocking as the most likely
       * source.
       */
      'Failed to fetch',
      /**
       * Apparently this is a benign error. It triggers *a lot* of Sentry noise. We don't use ResizeObserver directly, so it could
       * be coming from a dependency (possibly the nav, since it seems to happen across a variety of pages)
       *
       * https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded/50387233#50387233
       */
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      /**
       * This is thrown by `importScripts` when loading the source view of a file
       * using Monaco. It happens when the worker script fails to load, which doesn't
       * affect the UX and is extremely high volume.
       */
      "Failed to execute 'importScripts' on 'WorkerGlobalScope'",
      /**
       * This is a widely reported issue with the Sentry SDK. It happens when an exception is captured with a plain object instead of a
       * proper Error object. The Sentry SDK doesn't handle this case well, and it results in a lot of noise in Sentry. In our case, it
       * is caused by something within the Sentry SDK itself capturing an exception with an Event instance. The keys passed in aren't consistent
       * and sometimes include a `_pendo_` key and sometimes don't, so this filters out any exception that stems from a plain object.
       *
       * We will need to revisit this once we upgrade the Sentry SDK since newer versions handle this case differently.
       *
       * See https://github.com/getsentry/sentry-javascript/issues/2546
       */
      new RegExp('Non-Error exception captured .*'),
    ];

    const nrWrapperPattern = /\bnrWrapper\b/;

    const rewriteFrames = new Integrations.RewriteFrames({
      iteratee: frame => {
        // 'nrWrapper' is from NewRelic and in the stack trace, it would show up with the filename being the current URL
        // (including things like the repository slug). That meant that every stack trace was different, breaking Sentry's
        // grouping of errors. So rewrite those frames to fix that, see https://github.com/getsentry/sentry/issues/7709
        if (frame.function && nrWrapperPattern.test(frame.function)) {
          frame.filename = '?';
          frame.lineno = 0;
          frame.colno = 0;
          frame.in_app = false;
        }
        return frame;
      },
    });

    // After React 18 upgrade we get a lot of console errors when running locally.
    // This is because React 18 uses the console API to log errors in development mode.
    // When running Sentry locally, if we include console errors we'll get a 413
    // error because the payload is too large. Once we fix the local errors we can
    // remove this integration.
    const localIntegrations =
      window.__sentry__.environment === 'local'
        ? [
            new Sentry.Integrations.Breadcrumbs({
              console: false,
            }),
          ]
        : [];

    Sentry.init({
      ...config,
      release: process.env.FRONTBUCKET_SENTRY_RELEASE,
      denyUrls,
      ignoreErrors,
      integrations: [
        new Integrations.Dedupe(),
        new Integrations.ExtraErrorData(),
        rewriteFrames,
        ...localIntegrations,
      ],
    });

    // After Sentry is initialized, set additional user info for all events
    // eslint-disable-next-line no-restricted-properties
    const appDataUser = window.__app_data__?.user;

    // set user info in Sentry's global scope if the user is defined.
    // If user is not defined, it's an anonymous user.
    if (appDataUser) {
      // pick specific fields so we don't accidentally leak any sensitive data
      Sentry.setUser({
        id: appDataUser.uuid,
        aaid: appDataUser.aaid,
        isAtlassianStaff: appDataUser.isAtlassian,
        isBitbucketStaff: appDataUser.isBitbucket,
      });
    }

    // Add additional tag for better tag filtering in Sentry's UI
    Sentry.setTag(
      'userType',
      appDataUser
        ? appDataUser.isAtlassian
          ? 'INTERNAL'
          : 'EXTERNAL'
        : 'ANONYMOUS'
    );
  }
}

// call this immediately to initialize Sentry in the app
initializeSentry();
